<template>
  <span v-if="elapsedTime" v-bind="$attrs" class="elapsed-time">{{ elapsedTime }}</span>
  <span v-else-if="showEmptyState" v-bind="$attrs">{{ emptyStateText }}</span>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted, watch } from 'vue';
import { DateTime, Duration } from 'luxon';

function formatElapsedTime(diff, format) {
  if (diff.as('minutes') < 1) {
    return Duration.fromMillis(1 * 60 * 1000).toFormat(format);
  }

  return diff.toFormat(format);
}

function humanizeElapsedTime(diff) {
  if (diff.as('minutes') < 1) {
    return '1 minute';
  }

  // For human format, truncate decimals by creating a new Duration from truncated values
  const normalized = diff.shiftTo('days', 'hours', 'minutes');

  // Only include non-zero values to avoid showing "0 days, 0 hours, and X minutes"
  const durationObject = {};
  const days = Math.floor(normalized.days);
  const hours = Math.floor(normalized.hours);
  const minutes = Math.floor(normalized.minutes);

  if (days > 0) {
    durationObject.days = days;
  }

  if (hours > 0) {
    durationObject.hours = hours;
  }

  if (minutes > 0) {
    durationObject.minutes = minutes;
  }

  const truncatedDuration = Duration.fromObject(durationObject);

  return truncatedDuration.toHuman({ listStyle: 'long', showZeros: false });
}

function calculateElapsedTime(
  from,
  timezone,
  { format, variant, until } = {
    variant: 'formatted',
    format: 'hh:mm',
    until: null
  }
) {
  if (!from) {
    return '';
  }

  const start = DateTime.fromISO(from, { zone: timezone });
  const end = until
    ? DateTime.fromISO(until, { zone: timezone })
    : DateTime.now().setZone(timezone);

  const diff = end.diff(start, ['days', 'hours', 'minutes'], {
    conversionAccuracy: 'longterm'
  });

  if (variant === 'formatted') {
    return formatElapsedTime(diff, format);
  }

  return humanizeElapsedTime(diff);
}

export default defineComponent({
  name: 'ElapsedTime',
  props: {
    /**
     * The ISO date string to calculate elapsed time from
     */
    from: {
      type: String,
      required: true
    },
    /**
     * The ISO date string to calculate elapsed time until.
     * If not provided, defaults to current time (DateTime.now())
     */
    until: {
      type: String,
      required: false,
      default: null
    },
    timezone: {
      type: String,
      required: false
    },
    variant: {
      type: String,
      default: 'formatted',
      validator: value => ['formatted', 'human'].includes(value)
    },
    format: {
      type: String,
      default: 'hh:mm'
    },
    /**
     * Update frequency in milliseconds (default: 60000 = 1 minute)
     * Common values:
     * - 1000 = 1 second (for very frequent updates)
     * - 10000 = 10 seconds (for frequent updates)
     * - 30000 = 30 seconds (for moderate updates)
     * - 60000 = 1 minute (default, good balance)
     * - 300000 = 5 minutes (for infrequent updates)
     */
    updateInterval: {
      type: Number,
      default: 60000,
      validator: value => value >= 0
    },
    /**
     * Text to show when no elapsed time is available
     */
    emptyStateText: {
      type: String,
      default: '-'
    },
    /**
     * Whether to show empty state text when no date is provided
     */
    showEmptyState: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const elapsedTime = ref('');
    let intervalId = null;

    const updateElapsedTime = () => {
      if (!props.from) {
        elapsedTime.value = '';
        return;
      }

      try {
        elapsedTime.value = calculateElapsedTime(props.from, props.timezone, {
          format: props.format,
          variant: props.variant,
          until: props.until
        });
      } catch (error) {
        console.warn('ElapsedTime: Error calculating elapsed time:', error);
        elapsedTime.value = '';

        return false;
      }

      return true;
    };

    const startInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
      }

      if (!updateElapsedTime()) {
        return;
      }

      // Only start interval if 'until' is not provided (i.e., using current time)
      // If 'until' is provided, the elapsed time is fixed and doesn't need updates
      if (props.updateInterval > 0 && !props.until) {
        intervalId = setInterval(updateElapsedTime, props.updateInterval);
      }
    };

    const stopInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    watch(
      () => props.updateInterval,
      () => {
        if (intervalId) {
          startInterval();
        }
      }
    );

    watch([() => props.from, () => props.until], () => startInterval());

    onMounted(() => {
      startInterval();
    });

    onUnmounted(() => {
      stopInterval();
    });

    return {
      elapsedTime
    };
  }
});
</script>

<style scoped lang="scss">
.elapsed-time {
  // this is just to avoid layout shift as the time progresses
  min-inline-size: 72px;
}
</style>
